import { useCallback, useEffect, useState } from 'react';
import { ClipboardText } from 'shared/icons';
import api from '../../../services/api';
import { FormatDateTimeBr } from '../../../utils/date';
import { FormatPrice } from '../../../utils/format';
import { MLB_URLS } from '../../../utils/ml';
import { FmtInt, LegacyEmptyState, LegacyFilterPills, LegacyLoading, LegacyStatCard, Unwrap } from './legacyShared';

/**
 * Aba Legacy "Relatórios" — cópia de reports.html do Campaign Manager.
 * 5 stats → sub-seções: Sem Campanha · Perdendo Catálogo · Compartilhando
 * Catálogo · Mudanças de Campanhas (24h). Mesmas colunas das tabelas originais.
 */
const fmtBRL = (v) => FormatPrice(v);

const MLB_LINK = (id) => MLB_URLS.search(id);

const LegacyReports = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [summary, setSummary] = useState(null);
    const [withoutCampaign, setWithoutCampaign] = useState([]);
    const [losing, setLosing] = useState([]);
    const [sharing, setSharing] = useState([]);
    const [changes, setChanges] = useState([]);
    const [section, setSection] = useState('without-campaign');

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const [sum, wo, lo, sh, ch] = await Promise.all([
                api.get('/legacy-campaigns/reports/summary'),
                api.get('/legacy-campaigns/reports/without-campaign'),
                api.get('/legacy-campaigns/reports/losing-catalog'),
                api.get('/legacy-campaigns/reports/sharing-catalog'),
                api.get('/legacy-campaigns/reports/campaign-changes?hours=24'),
            ]);
            setSummary(Unwrap(sum) || null);
            setWithoutCampaign(Unwrap(wo) || []);
            setLosing(Unwrap(lo) || []);
            setSharing(Unwrap(sh) || []);
            setChanges(Unwrap(ch) || []);
        } catch (err) {
            setError(err.message || 'Erro ao carregar relatórios');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (loading) return <LegacyLoading text="Carregando relatórios..." />;
    if (error) return <LegacyEmptyState icon={ClipboardText} text={`Erro: ${error}`} />;

    const changeTypeBadge = (type) => {
        const map = {
            campaign_activated: ['Ativada', 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'],
            campaign_deactivated: ['Desativada', 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'],
            campaign_changed: ['Alterada', 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'],
        };
        const [label, cls] = map[type] || [type, 'bg-mono-100 text-mono-600 dark:bg-mono-800 dark:text-mono-300'];
        return <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>{label}</span>;
    };

    const skuChip = (sku) => (
        <span className="rounded-md bg-mono-100 px-1.5 py-px font-mono text-[11px] text-mono-600 dark:bg-mono-900 dark:text-mono-300">
            {sku || 'N/A'}
        </span>
    );
    const mlbLink = (id) => (
        <a href={MLB_LINK(id)} target="_blank" rel="noreferrer" className="font-mono text-xs text-red-600 hover:underline dark:text-red-400">
            {id}
        </a>
    );

    return (
        <div className="space-y-4">
            {/* 5 stats do summary */}
            <div className="grid grid-cols-2 gap-2.5 md:grid-cols-5">
                <LegacyStatCard label="Total Produtos" value={FmtInt(summary?.total_products)} />
                <LegacyStatCard label="Com Campanha" value={FmtInt(summary?.with_campaign)} />
                <LegacyStatCard label="Sem Campanha" value={FmtInt(summary?.without_campaign)} />
                <LegacyStatCard label="Perdendo Catálogo" value={FmtInt(summary?.losing_catalog)} />
                <LegacyStatCard label="Compartilhando" value={FmtInt(summary?.sharing_catalog)} />
            </div>

            {/* sub-seções (nav-tabs do legado) */}
            <LegacyFilterPills
                pills={[
                    { id: 'without-campaign', label: 'Sem Campanha', count: withoutCampaign.length },
                    { id: 'losing-catalog', label: 'Perdendo Catálogo', count: losing.length },
                    { id: 'sharing-catalog', label: 'Compartilhando Catálogo', count: sharing.length },
                    { id: 'campaign-changes', label: 'Mudanças de Campanhas', count: changes.length },
                ]}
                active={section}
                onSelect={setSection}
            />

            {section === 'without-campaign' && (
                <ReportTable
                    title="Produtos Sem Campanha"
                    headers={['SKU', 'MLB', 'Título', 'Preço', 'Estoque']}
                    aligns={['left', 'left', 'left', 'right', 'right']}
                    empty="Nenhum produto sem campanha"
                    rows={withoutCampaign.map((p) => [
                        skuChip(p.sku),
                        mlbLink(p.id),
                        <span key="t" className="text-black dark:text-white">{(p.title || '').substring(0, 60)}</span>,
                        <span key="p" className="font-semibold">{fmtBRL(p.price)}</span>,
                        p.available_stock || 0,
                    ])}
                />
            )}

            {section === 'losing-catalog' && (
                <ReportTable
                    title="Produtos Perdendo Catálogo"
                    headers={['SKU', 'MLB', 'Título', 'Preço Atual', 'Preço p/ Ganhar', 'Diferença', 'Ganhador']}
                    aligns={['left', 'left', 'left', 'right', 'right', 'right', 'left']}
                    empty="Nenhum produto perdendo catálogo"
                    rows={losing.map((p) => [
                        skuChip(p.sku),
                        mlbLink(p.item_id),
                        <span key="t" className="text-black dark:text-white">{(p.title || '').substring(0, 45)}</span>,
                        <span key="c" className="font-bold">{fmtBRL(p.current_price)}</span>,
                        <span key="w" className="text-green-600 dark:text-green-400">{fmtBRL(p.price_to_win)}</span>,
                        <span key="d" className="font-bold text-red-500">-{fmtBRL(Math.abs(p.price_difference))}</span>,
                        p.winner_seller_nickname || '-',
                    ])}
                />
            )}

            {section === 'sharing-catalog' && (
                <ReportTable
                    title="Produtos Compartilhando 1º Lugar"
                    headers={['SKU', 'MLB', 'Título', 'Preço', 'Concorrentes', 'Visit Share']}
                    aligns={['left', 'left', 'left', 'right', 'center', 'center']}
                    empty="Nenhum produto compartilhando catálogo"
                    rows={sharing.map((p) => [
                        skuChip(p.sku),
                        mlbLink(p.item_id),
                        <span key="t" className="text-black dark:text-white" title={p.competitor_names || ''}>
                            {(p.title || '').substring(0, 45)}
                        </span>,
                        fmtBRL(p.current_price),
                        <span key="n" className="inline-flex rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
                            {p.competitors_sharing_first_place}
                        </span>,
                        p.visit_share || '-',
                    ])}
                />
            )}

            {section === 'campaign-changes' && (
                <ReportTable
                    title="Mudanças de Campanhas (24h)"
                    headers={['Tipo', 'SKU', 'MLB', 'Campanha', 'Data/Hora']}
                    aligns={['left', 'left', 'left', 'left', 'left']}
                    empty="Nenhuma mudança nas últimas 24h"
                    rows={changes.map((n) => {
                        const meta = typeof n.metadata === 'string' ? JSON.parse(n.metadata || '{}') : n.metadata || {};
                        return [
                            changeTypeBadge(n.type),
                            skuChip(meta.sku),
                            mlbLink(n.product_id),
                            <span key="c" className="text-black dark:text-white">{n.campaign_name || '-'}</span>,
                            FormatDateTimeBr(n.created_at, { blank: '-' }),
                        ];
                    })}
                />
            )}
        </div>
    );
};

const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' };

const ReportTable = ({ title, headers, aligns, rows, empty }) => (
    <div className="overflow-hidden rounded-xl border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950">
        <div className="flex items-center justify-between border-b border-mono-100 px-4 py-2.5 dark:border-mono-900">
            <p className="text-sm font-semibold text-black dark:text-white">{title}</p>
            <span className="rounded-full bg-mono-100 px-2 py-0.5 text-xs font-semibold text-mono-600 dark:bg-mono-800 dark:text-mono-300">
                {rows.length}
            </span>
        </div>
        <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-xs">
                <thead>
                    <tr className="border-b border-mono-100 dark:border-mono-900">
                        {headers.map((h, i) => (
                            <th key={h} className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-mono-400 ${ALIGN[aligns[i]]}`}>
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={headers.length} className="px-3 py-8 text-center text-mono-500">
                                {empty}
                            </td>
                        </tr>
                    ) : (
                        rows.slice(0, 300).map((cells, ri) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <tr key={ri} className="border-b border-mono-50 last:border-b-0 hover:bg-mono-50 dark:border-mono-900/60 dark:hover:bg-mono-900/40">
                                {cells.map((cell, ci) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <td key={ci} className={`px-3 py-1.5 tabular-nums text-mono-600 dark:text-mono-300 ${ALIGN[aligns[ci]]}`}>
                                        {cell}
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    </div>
);

export default LegacyReports;
